/** Utils */
import { type FC, type ReactNode, useCallback, useEffect, useMemo, useReducer } from 'react';
import AuthContext, { authInitialState } from '../context/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { mutate } from 'swr';

import useAuthInit from '../hooks/useAuthInit';
import useAuthSync from '../hooks/useAuthSync';
import useTokenRefresh from '../hooks/useTokenRefresh';
import AuthService from '../service/AuthService';
import authReducer from '../reducer/AuthReducer';
import type {
  AccountActivationError,
  LoginError,
  ResetPasswordResponse,
} from '@/interfaces/api/app';
import FetchError from '@/shared/utils/api/FetchError';

/** Types */
import type {
  ActivateAccountProps,
  B2BRegistrationProps,
  ClearStorageProps,
  RegistrationProps,
  CredentialsLoginProps,
  ForgotPasswordProps,
  //GetProfileProps,
  ProviderLogoutProps,
  ResetPasswordProps,
  SetErrorProps,
  SetStorageProps,
} from '../Auth.types';
import { ErrorNotification } from '@/shared/components/Notifications/ErrorNotification/ErrorNotification';
//import type { User } from '@/interfaces/api/account';
import { logoutChannel } from '../config/logout-channel';
import { isAllowedAccountType } from '../utils/isAllowedAccountType';

/**
 *
 * @method login
 * @method logout
 * @method register
 * @method getProfile
 * @method forgotPassword
 * @method resetPassword
 *
 * Utils
 * @method setError
 * @method clearStorage
 * @method setStorage
 */
const AuthProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, authInitialState);

  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Utility to set error message only.
   *
   * @param {string} props.message
   *
   * @returns {Promise<void>}
   */
  const setError = useCallback((props: SetErrorProps): void => {
    dispatch({ type: 'SET_ERROR', payload: { message: props.message } });
  }, []);

  /**
   * Utility to clear tokens from storage (sync with other open tabs).
   *
   * @param {void} props.onComplete - action that runs on tasks complete.
   */
  const clearStorage = (props: ClearStorageProps) => {
    const { onComplete } = props;

    setTimeout(() => {
      // Clear tokens and dispatch a storage event.
      AuthService.clearTokens();

      // Notify other tabs a logout is happening.
      logoutChannel.postMessage('logout');

      // Reset `isLoggingOut` after a short delay
      setTimeout(() => {
        dispatch({ type: 'BROADCASTING_LOGOUT', payload: { loading: false } });

        if (onComplete) {
          onComplete();
        }
      }, 100);
    }, 50);
  };

  /**
   * Utility to set tokens in storage (sync with other open tabs).
   *
   * @param {string} props.accessToken
   * @param {string} props.refreshToken
   * @param {void} props.onComplete - optional: action that runs on task complete.
   */
  const setStorage = (props: SetStorageProps) => {
    const { accessToken, refreshToken, csrfToken, onComplete } = props;

    AuthService.setTokens({ accessToken, refreshToken, csrfToken });

    // Reset this toast once per login
    // authStorage.setItem('zinzo.company_details_toast', 'not-skipped');
    // authStorage.setItem('zinzo.certificate_toast', 'not-skipped');

    // Trigger storage event after setting both tokens
    setTimeout(() => {
      window.dispatchEvent(new Event('storage'));
    }, 50);

    if (onComplete) {
      onComplete();
    }
  };

  /**
   * Logs user out from backend and frontend, and wipes access/refresh tokens from `authStorage`.
   * It also notifies other open tabs that they should logout user through a `MessageEvent`.
   *
   * @param {string} props.redirectTo - Optional: redirects to this url if passed
   */
  const logout = useCallback(
    async (props: ProviderLogoutProps) => {
      const { redirectTo } = props;

      try {
        // Prevent multiple logout attempts
        if (state.isLoggingOut) {
          return;
        }

        // Notify other tabs a logout is in progress
        dispatch({
          type: 'BROADCASTING_LOGOUT',
          payload: { loading: true },
        });

        // Skip backend logout if token is corrupted
        if (state.accessToken) {
          try {
            await AuthService.logout({ accessToken: state.accessToken });
          } catch (error) {
            if (error instanceof FetchError) {
              console.error('Failed to logout from backend: ', error.error.message);
            } else {
              console.error('Failed to logout from backend.');
            }
          }
        }

        if (state.refreshToken) {
          try {
            await AuthService.logout({ refreshToken: state.refreshToken });
          } catch (error) {
            if (error instanceof FetchError) {
              console.error('Failed to logout from backend: ', error.error.message);
            } else {
              console.error('Failed to logout from backend.');
            }
          }
        }

        // Wipe reducer first
        dispatch({ type: 'LOGOUT' });

        // Then clear storage
        clearStorage({
          onComplete: () => {
            if (redirectTo) {
              navigate(redirectTo);
            }
          },
        });
        mutate(() => true, undefined, false);
      } catch (error) {
        if (error instanceof FetchError) {
          const errorObject = error.error;

          switch (errorObject.status) {
            case 422:
              // UNPROCESSABLE_ENTITY: corrupted refresh token,
              // wipe user.
              // It reaches this scope only when trying to logout from backend
              // using a corrupted refresh token, that's why this check is needed.

              dispatch({ type: 'LOGOUT' });
              clearStorage({
                onComplete: () => {
                  if (redirectTo) {
                    navigate(redirectTo);
                  }
                },
              });
              break;

            default:
              console.error('Unhandled logout error: ', errorObject);
              setError({ message: 'Failed to logout.' });
          }
        }

        dispatch({
          type: 'BROADCASTING_LOGOUT',
          payload: { loading: false },
        });
      }
    },
    [navigate, setError, state.accessToken, state.isLoggingOut]
  );

  /**
   * Sends a login request to backend, then sets access/refresh tokens in
   * `authStorage` - which is either localStorage or sessionStorage - then triggers
   * a storage event with a timeout of 50ms, then sends a request to fetch user' profile based
   * on access token currently in storage.
   *
   * If AuthService.login returns an external url, the flow is interrupted and page will be
   * redirected to `response['url']`.
   *
   *
   * @param {string} props.email
   * @param {string} props.password
   * @param {string} props.multipass - optional
   * @param {string} props.redirectTo - optional - Practitioner or Client
   */
  const login = useCallback(
    async (props: CredentialsLoginProps): Promise<void> => {
      const { email, password, redirectTo } = props;

      try {
        const { access_token: accessToken, refresh_token: refreshToken } = await AuthService.login({
          email,
          password,
        });

        // The token will exist from the `login` request trigger in `authStorage`, since the request can't be made without one
        const { csrfToken } = AuthService.getTokens();

        if (!accessToken || !refreshToken) {
          // A redirect has occurred, don't proceed
          return;
        }

        setStorage({ accessToken, refreshToken });

        const user = await AuthService.getUser({ accessToken });

        if (!isAllowedAccountType(user.account_type)) {
          AuthService.clearTokens();
          setError({ message: 'Access denied. This portal is for B2B clients only.' });
          return;
        }

        dispatch({
          type: 'LOGIN',
          payload: {
            user,
            accessToken,
            refreshToken,
            csrfToken: csrfToken ?? (await AuthService.CSRFToken()).csrf_token,
          },
        });

        navigate(redirectTo ?? '/dashboard');
      } catch (error) {
        if (error instanceof FetchError) {
          console.error('Login error: ', error.error);

          const statusCode = error.error.status;
          const errorResponse = (await error.error.json()) as LoginError;

          switch (statusCode) {
            case 400:
              // BAD REQUEST: probably bad username or password.

              setError({ message: errorResponse.message || 'Invalid username or password' });
              ErrorNotification(errorResponse.message);
              break;

            case 401:
              // UNAUTHORIZED: either invalid credentials or expired session.
              // If user has no active session (no tokens), treat as invalid credentials.
              // Otherwise, session expired — wipe tokens and state.

              if (!state.accessToken && !state.refreshToken) {
                setError({ message: errorResponse.message || 'Invalid email or password' });
                ErrorNotification(errorResponse.message || 'Invalid email or password');
              } else {
                dispatch({ type: 'LOGOUT' });
                clearStorage({});
              }
              break;

            case 422: {
              // UNPROCESSABLE_ENTITY: Access token is probably corrupted or expired, attempt a refresh.

              try {
                if (!state.refreshToken) {
                  // No refresh token present means tokens are gone, log user out in catch block.
                  throw new Error('No refresh token stored.');
                }

                // Get new tokens with refresh token
                const { access_token: accessToken, refresh_token: refreshToken } =
                  await AuthService.refreshTokens({
                    refreshToken: state.refreshToken,
                  });

                AuthService.setTokens({ accessToken, refreshToken });

                dispatch({ type: 'REVALIDATE_TOKENS', payload: { accessToken, refreshToken } });
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
              } catch (error) {
                // Failed to revalidate, logout user.
                await logout({});
              }

              break;
            }

            case 500:
              // INTERNAL_SERVER_ERROR: backend can't use tokens, just remove them.

              await logout({});
              break;

            default:
              console.error('Unhandled login error: ', errorResponse.message);
              await logout({});
              dispatch({
                type: 'SET_ERROR',
                payload: { message: 'Unhandled login error, check console for details' },
              });
              break;
          }
        } else if (error instanceof Error) {
          console.error('Login error: ', error.message);
          setError({ message: error.message || 'Invalid email or password' });
        } else {
          console.error('Unknown error occurred during login: ', error);
          setError({ message: 'An unexpected error occurred' });
          ErrorNotification('An unexpected error occurred');
        }
      }
    },
    [logout, navigate, setError, state.accessToken, state.refreshToken]
  );

  /**
   * Registers a company`.
   *
   * @param {string} props.redirectTo - optional.
   */
  const register = useCallback(
    async (props: RegistrationProps) => {
      const { redirectTo, ...rest } = props;

      try {
        await AuthService.register({ ...rest });

        if (redirectTo) {
          // Pass the email in the state
          navigate(redirectTo, { state: { from: location, email: rest.email } });
        }
      } catch (error) {
        if (error instanceof FetchError) {
          const errorObject = (await error.error.json()) as { message: string };
          setError({ message: errorObject.message || 'An unexpected error occurred.' });
          ErrorNotification(errorObject.message);
          return;
        }

        console.error(`Error registering user: `, error);
        setError({ message: 'An unexpected error occurred.' });
      }
    },
    [location, navigate, setError]
  );

  /**
   * Registers a B2B client.
   */
  const b2bRegister = useCallback(
    async (props: B2BRegistrationProps) => {
      const { redirectTo, ...rest } = props;

      try {
        await AuthService.b2bRegister({ ...rest });

        if (redirectTo) {
          navigate(redirectTo, { state: { from: location, email: rest.email } });
        }
      } catch (error) {
        if (error instanceof FetchError) {
          const errorObject = (await error.error.json()) as { message: string };
          setError({ message: errorObject.message || 'An unexpected error occurred.' });
          ErrorNotification(errorObject.message);
          return;
        }

        console.error(`Error registering B2B user: `, error);
        setError({ message: 'An unexpected error occurred.' });
      }
    },
    [location, navigate, setError]
  );

  /**
   * Activates user account after registration.
   *
   * @param {string} props.token
   */
  const activateAccount = useCallback(async (props: ActivateAccountProps) => {
    const { token } = props;

    try {
      const { access_token, refresh_token } = await AuthService.activateAccount({ token });
      const { csrf_token } = await AuthService.CSRFToken();
      setStorage({
        accessToken: access_token,
        refreshToken: refresh_token,
        csrfToken: csrf_token,
      });

      const user = await AuthService.getUser({ accessToken: access_token });

      if (!isAllowedAccountType(user.account_type)) {
        AuthService.clearTokens();
        throw new Error('Access denied. This portal is for B2B clients only.');
      }

      dispatch({
        type: 'LOGIN',
        payload: {
          user,
          accessToken: access_token,
          refreshToken: refresh_token,
          csrfToken: csrf_token,
        },
      });
    } catch (error) {
      if (error instanceof FetchError) {
        const errorObject = (await error.error.json()) as AccountActivationError;
        throw new Error(errorObject.message || 'An unexpected error occurred.');
      } else {
        throw new Error('An unexpected error occurred.');
      }
    }
  }, []);

  /**
   * Fetches profile for the logged in user.
   * First time it sends a request, subsequent ones are stored in Reducer,
   * and wiped on logout/login/reload.
   *
   * Will send a new request if `refreshProfile` is specified
   *
   * @param {string} props.accessToken
   */
  /*   const getProfile = useCallback(
    async (props: GetProfileProps): Promise<User | undefined> => {
      const { accessToken, refreshProfile = false } = props;

      let profile;

      try {
        // Check if profile is already stored and a refresh is not specified.
        if (state.profile && !refreshProfile) {
          return state.profile;
        }

        profile = await AuthService.getProfile({ accessToken });

        // Set profile so it can be reused
        dispatch({ type: 'SET_PROFILE', payload: { profile } });

        return profile;
      } catch (error) {
        console.error(error);
        setError({ message: `Unable to fetch profile` });
      }
    },
    [setError, state.profile]
  ); */

  /**
   * Sends a forgot password request
   *
   * @param {string} props.email
   */
  const forgotPassword = useCallback(
    async (props: ForgotPasswordProps) => {
      const { email } = props;

      try {
        await AuthService.CSRFToken();
        const message = await AuthService.forgotPassword({ email });

        return message;
      } catch (error) {
        console.error(error);
        setError({
          message:
            'If your email was found in our system, a password reset link will be sent to you.',
        });
      }
    },
    [setError]
  );

  const resetPassword = useCallback(
    async (props: ResetPasswordProps): Promise<ResetPasswordResponse> => {
      const { password, confirm_password, resetPasswordToken, redirectTo } = props;

      try {
        await AuthService.CSRFToken();

        const { access_token, refresh_token, user_type_id } = await AuthService.resetPassword({
          password,
          confirm_password,
          resetPasswordToken,
        });

        const { csrfToken: csrf_token = '' } = AuthService.getTokens();

        setStorage({
          accessToken: access_token,
          refreshToken: refresh_token,
          csrfToken: csrf_token,
        });

        const user = await AuthService.getUser({ accessToken: access_token });

        if (!isAllowedAccountType(user.account_type)) {
          AuthService.clearTokens();
          setError({ message: 'Access denied. This portal is for B2B clients only.' });
          return { access_token, refresh_token, user_type_id };
        }

        dispatch({
          type: 'LOGIN',
          payload: {
            user,
            accessToken: access_token,
            refreshToken: refresh_token,
            csrfToken: csrf_token,
          },
        });

        navigate(redirectTo ?? '/dashboard');

        return { access_token, refresh_token, user_type_id };
      } catch (error) {
        console.error(error);
        let message = 'An unexpected error occurred.';

        if (error instanceof FetchError && error.error instanceof Response) {
          try {
            const data = await error.error.clone().json();

            if (data.message) {
              message = data.message;
            }
          } catch {
            message = 'Something went wrong. Please try again.';
          }
        } else if (error instanceof Error) {
          message = error.message;
        }

        setError({ message });
        throw error;
      }
    },
    [navigate, setError]
  );

  /**
   * Mutates user in the reducer state.
   */
  const mutateUser = useCallback(
    async (accessToken?: string) => {
      try {
        const token = accessToken ?? state.accessToken;
        if (!token) {
          throw new Error('Failed to mutate user: No access token stored.');
        }

        const user = await AuthService.getUser({
          accessToken: token,
        });

        dispatch({
          type: 'SET_USER',
          payload: { user },
        });
      } catch (error) {
        console.error(error);
      }
    },
    [state.accessToken]
  );

  // Always restart error when page is navigated
  useEffect(() => {
    setError({ message: undefined });
  }, [navigate, setError]);

  const contextValues = useMemo(
    () => ({
      ...state,
      dispatch,
      login,
      logout,
      register,
      b2bRegister,
      activateAccount,
      //getProfile,
      forgotPassword,
      resetPassword,
      setError,
      clearStorage,
      mutateUser,
    }),
    [
      state,
      login,
      logout,
      register,
      b2bRegister,
      activateAccount,
      //getProfile,
      forgotPassword,
      resetPassword,
      setError,
      mutateUser,
    ]
  );

  // Initialize provider with contextValues and initialization hooks
  useAuthInit(contextValues);

  // Synchronize tabs with useAuthSync
  useAuthSync(contextValues);

  // Attach token refresh mechanism
  useTokenRefresh({
    state: state.state,
    isLoggingOut: state.isLoggingOut,
    setError,
    logout,
  });

  return <AuthContext.Provider value={contextValues}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
